/**
 * Talents store — local-only Sirus 3.3.5a-style talent allocations.
 *
 * Sirus parity model:
 *   – 3 spec tabs per class.
 *   – 28 talent slots (7 rows × 4 cols) per spec.
 *   – 71 total talent points by level 80 (WotLK 3.3.5a baseline).
 *
 * Server is authoritative for the *real* spec stat-recalc pipeline
 * (see `packages/aiwari-server/src/data/talents-server.ts` and the
 * legacy `TalentTree.tsx` DBC-driven flow). This store is intentionally
 * client-side-only — the new Sirus-styled `sirus/TalentTree.tsx` panel
 * is a UI shell that lets the player play with allocations until the
 * "save build" → server-sync wire-up lands in a later task.
 *
 * Persistence: `localStorage['aiwari-talent-state-<classId>']` keeps the
 * current per-spec allocation map across reloads. Named saved builds use
 * `localStorage['aiwari-talent-builds-<classId>']` (an array of
 * `{ name, allocations, spec }`). The store does NOT depend on either
 * directly — `sirus/TalentTree.tsx` owns the named-build UI and uses the
 * `set()` method to load a build into the active map.
 */

import { create } from 'zustand';

export const TALENT_TOTAL_POINTS = 71;
export const TALENT_SPECS_PER_CLASS = 3;
export const TALENT_ROWS = 7;
export const TALENT_COLS = 4;

/** Per-class allocation: spec index (0..2) → talentId → rank. */
export type SpecAllocations = Record<number, Record<string, number>>;

interface TalentsStore {
  /** classId → spec → talentId → rank. */
  allocations: Record<string, SpecAllocations>;
  /** Cached "spent points" total per class — recomputed on every set/reset. */
  spent: Record<string, number>;
  /**
   * Add 1 rank to a talent. `maxRank` clamps the upper bound (caller knows
   * each talent's max ranks from the catalogue). No-op if at cap or if
   * the player has no points left (spent < TALENT_TOTAL_POINTS).
   */
  addRank: (classId: string, spec: number, talentId: string, maxRank: number) => void;
  /** Remove 1 rank — clamped at 0. */
  removeRank: (classId: string, spec: number, talentId: string) => void;
  /** Bulk-load an allocation set (used by save/load build flow). */
  set: (classId: string, allocations: SpecAllocations) => void;
  /** Wipe every rank for the class. */
  reset: (classId: string) => void;
  /** Read the rank of a single talent (0 if unset). */
  rankOf: (classId: string, spec: number, talentId: string) => number;
  /** Read the "spent in spec" subtotal — used for tier unlock gating. */
  spentInSpec: (classId: string, spec: number) => number;
}

function sumAllocations(alloc: SpecAllocations): number {
  let total = 0;
  for (const spec of Object.values(alloc)) {
    for (const r of Object.values(spec)) total += r;
  }
  return total;
}

export const useTalents = create<TalentsStore>((set, get) => ({
  allocations: {},
  spent: {},
  addRank: (classId, spec, talentId, maxRank) => {
    const state = get();
    const totalSpent = state.spent[classId] ?? 0;
    if (totalSpent >= TALENT_TOTAL_POINTS) return;
    const classAlloc = state.allocations[classId] ?? {};
    const specAlloc = classAlloc[spec] ?? {};
    const cur = specAlloc[talentId] ?? 0;
    if (cur >= maxRank) return;
    const nextSpec = { ...specAlloc, [talentId]: cur + 1 };
    const nextClass = { ...classAlloc, [spec]: nextSpec };
    set({
      allocations: { ...state.allocations, [classId]: nextClass },
      spent: { ...state.spent, [classId]: totalSpent + 1 },
    });
  },
  removeRank: (classId, spec, talentId) => {
    const state = get();
    const classAlloc = state.allocations[classId] ?? {};
    const specAlloc = classAlloc[spec] ?? {};
    const cur = specAlloc[talentId] ?? 0;
    if (cur <= 0) return;
    const nextRank = cur - 1;
    const nextSpec = { ...specAlloc };
    if (nextRank <= 0) delete nextSpec[talentId];
    else nextSpec[talentId] = nextRank;
    const nextClass = { ...classAlloc, [spec]: nextSpec };
    const totalSpent = Math.max(0, (state.spent[classId] ?? 0) - 1);
    set({
      allocations: { ...state.allocations, [classId]: nextClass },
      spent: { ...state.spent, [classId]: totalSpent },
    });
  },
  set: (classId, allocations) => {
    const state = get();
    const total = sumAllocations(allocations);
    set({
      allocations: { ...state.allocations, [classId]: allocations },
      spent: { ...state.spent, [classId]: total },
    });
  },
  reset: (classId) => {
    const state = get();
    const nextAlloc = { ...state.allocations };
    delete nextAlloc[classId];
    const nextSpent = { ...state.spent };
    delete nextSpent[classId];
    set({ allocations: nextAlloc, spent: nextSpent });
  },
  rankOf: (classId, spec, talentId) => {
    const state = get();
    return state.allocations[classId]?.[spec]?.[talentId] ?? 0;
  },
  spentInSpec: (classId, spec) => {
    const state = get();
    const specAlloc = state.allocations[classId]?.[spec] ?? {};
    let total = 0;
    for (const r of Object.values(specAlloc)) total += r;
    return total;
  },
}));
